/**
 * API Client — Wrapper para todas las llamadas al backend.
 */

const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const token = typeof window !== 'undefined' ? localStorage.getItem('teresita_session_token') : null;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as any),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Error desconocido' }));
      throw new Error((error as any).error || `HTTP ${response.status}`);
    }

    return response.json() as Promise<T>;
  }

  // ─── Tasks ───
  getTodayTasks() {
    return this.request<{ tasks: any[] }>('/tasks/today');
  }

  getTasks(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<{ tasks: any[] }>(`/tasks${query}`);
  }

  createTask(data: any) {
    return this.request<{ id: string }>('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateTask(id: string, data: any) {
    return this.request(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  updateTaskStatus(id: string, status: string) {
    return this.request(`/tasks/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  deleteTask(id: string) {
    return this.request(`/tasks/${id}`, { method: 'DELETE' });
  }

  // ─── Clients ───
  getClients(status = 'active') {
    return this.request<{ clients: any[] }>(`/clients?status=${status}`);
  }

  getClient(id: string) {
    return this.request<{ client: any }>(`/clients/${id}`);
  }

  createClient(data: any) {
    return this.request<{ id: string }>('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateClient(id: string, data: any) {
    return this.request(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  deleteClient(id: string) {
    return this.request(`/clients/${id}`, { method: 'DELETE' });
  }

  // ─── Client Google Drive ───
  createClientDriveFolder(id: string) {
    return this.request<{
      success: boolean;
      message: string;
      clientFolderId: string;
      monthFolderId: string;
      folderUrl: string;
    }>(`/clients/${id}/drive/create-folder`, { method: 'POST' });
  }

  linkClientDriveFolder(id: string, folderIdOrUrl: string) {
    return this.request<{
      success: boolean;
      folderId: string | null;
      folderUrl: string | null;
    }>(`/clients/${id}/drive/link`, {
      method: 'PATCH',
      body: JSON.stringify({ folder_id_or_url: folderIdOrUrl }),
    });
  }

  getClientDriveFiles(id: string, folderId?: string) {
    const query = folderId ? `?folderId=${encodeURIComponent(folderId)}` : '';
    return this.request<{
      connected: boolean;
      hasFolder: boolean;
      currentFolderId?: string;
      rootFolderId?: string;
      folderUrl?: string;
      message?: string;
      error?: string;
      files: Array<{
        id: string;
        name: string;
        mimeType: string;
        size?: string;
        modifiedTime?: string;
        webViewLink?: string;
        iconLink?: string;
        thumbnailLink?: string;
      }>;
    }>(`/clients/${id}/drive/files${query}`);
  }

  // ─── Time Entries ───
  getTimeEntries(clientId: string, month?: string) {
    const query = month ? `?month=${month}` : '';
    return this.request<{ timeEntries: any[] }>(
      `/clients/${clientId}/time-entries${query}`
    );
  }

  createTimeEntry(clientId: string, data: any) {
    return this.request<{ id: string }>(`/clients/${clientId}/time-entries`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ─── Finances ───
  getFinanceSummary(month?: string) {
    const query = month ? `?month=${month}` : '';
    return this.request<any>(`/finances/summary${query}`);
  }

  getFinances(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<{ finances: any[] }>(`/finances${query}`);
  }

  createFinance(data: any) {
    return this.request<{ id: string }>('/finances', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateFinance(id: string, data: any) {
    return this.request(`/finances/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  deleteFinance(id: string) {
    return this.request(`/finances/${id}`, { method: 'DELETE' });
  }

  // ─── Metrics ───
  getMetrics(clientId: string, params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<{ metrics: any[] }>(`/metrics/${clientId}${query}`);
  }

  saveMetrics(data: any) {
    return this.request('/metrics', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  importMetricsBatch(metrics: any[]) {
    return this.request('/metrics/batch', {
      method: 'POST',
      body: JSON.stringify({ metrics }),
    });
  }

  // ─── Content Calendar ───
  getContentCalendar(clientId: string, period?: string) {
    const query = period ? `?period=${period}` : '';
    return this.request<{ calendar: any[] }>(
      `/content/calendar/${clientId}${query}`
    );
  }

  createContentCalendar(data: any) {
    return this.request<{ id: string }>('/content/calendar', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateContentCalendar(id: string, data: any) {
    return this.request(`/content/calendar/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  deleteContentCalendar(id: string) {
    return this.request(`/content/calendar/${id}`, { method: 'DELETE' });
  }

  // ─── Marketing Actions ───
  getMarketingActions(clientId: string, period?: string) {
    const query = period ? `?period=${period}` : '';
    return this.request<{ actions: any[] }>(
      `/content/actions/${clientId}${query}`
    );
  }

  createMarketingAction(data: any) {
    return this.request<{ id: string }>('/content/actions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateMarketingAction(id: string, data: any) {
    return this.request(`/content/actions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  deleteMarketingAction(id: string) {
    return this.request(`/content/actions/${id}`, { method: 'DELETE' });
  }

  // ─── Production Items ───
  getProductionItems(clientId: string, period?: string) {
    const query = period ? `?period=${period}` : '';
    return this.request<{ production: any[] }>(
      `/content/production/${clientId}${query}`
    );
  }

  createProductionItem(data: any) {
    return this.request<{ id: string }>('/content/production', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateProductionItem(id: string, data: any) {
    return this.request(`/content/production/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  updateProductionItemStatus(id: string, status: string, sortOrder = 0) {
    return this.request(`/content/production/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, sort_order: sortOrder }),
    });
  }

  deleteProductionItem(id: string) {
    return this.request(`/content/production/${id}`, { method: 'DELETE' });
  }

  // ─── Content Copies ───
  getContentCopies(clientId: string, period?: string) {
    const query = period ? `?period=${period}` : '';
    return this.request<{ copies: any[] }>(
      `/content/copies/${clientId}${query}`
    );
  }

  createContentCopy(data: any) {
    return this.request<{ id: string }>('/content/copies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateContentCopy(id: string, data: any) {
    return this.request(`/content/copies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  deleteContentCopy(id: string) {
    return this.request(`/content/copies/${id}`, { method: 'DELETE' });
  }

  // ─── AI ───
  analyzeMetrics(metrics: any[]) {
    return this.request<{ analysis: string }>('/ai/analyze', {
      method: 'POST',
      body: JSON.stringify({ metrics }),
    });
  }

  generateCopy(data: any) {
    return this.request<{ copy: string }>('/ai/generate-copy', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  suggestContent(data: any) {
    return this.request<{ suggestions: string }>('/ai/suggest-content', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ─── Monthly Closing ───
  closeMonth(clientId: string, period: string) {
    return this.request<{ message: string; closingId: string; summary: string }>(
      '/monthly/close',
      {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId, period }),
      }
    );
  }

  getMonthlyHistory(clientId: string) {
    return this.request<{ closings: any[] }>(`/monthly/history/${clientId}`);
  }

  // ─── Autenticación de Usuario (Login independiente) ───
  getGoogleLoginUrl(redirectUri?: string) {
    const query = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '';
    return this.request<{ url: string }>(`/auth/login/google/url${query}`);
  }

  handleGoogleLoginCallback(code: string, redirectUri?: string) {
    return this.request<{
      success: boolean;
      message: string;
      session_token: string;
      user: { email: string; name: string; picture?: string };
    }>('/auth/login/google/callback', {
      method: 'POST',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
  }

  loginWithCredentials(email: string, password: string) {
    return this.request<{
      success: boolean;
      message: string;
      session_token: string;
      user: { email: string; name: string; picture?: string };
    }>('/auth/login/credentials', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  // ─── Integración de Google Drive (Compartida para el Equipo) ───
  getGoogleDriveUrl(redirectUri?: string) {
    const query = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '';
    return this.request<{ url: string }>(`/auth/drive/url${query}`);
  }

  handleGoogleDriveCallback(code: string, redirectUri?: string) {
    return this.request<{
      success: boolean;
      message: string;
      user: { email: string; name: string; picture?: string };
    }>('/auth/drive/callback', {
      method: 'POST',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
  }

  getGoogleDriveStatus() {
    return this.request<{
      connected: boolean;
      mode: 'oauth' | 'service_account' | 'none';
      user: { email: string; name: string; picture?: string } | null;
      connected_at?: string;
    }>('/auth/drive/status');
  }

  disconnectGoogleDrive() {
    return this.request<{ success: boolean; message: string }>('/auth/drive/disconnect', {
      method: 'POST',
    });
  }

  // Aliases retrocompatibles
  getGoogleAuthUrl(redirectUri?: string) {
    return this.getGoogleDriveUrl(redirectUri);
  }

  handleGoogleCallback(code: string, redirectUri?: string, state?: string) {
    if (state === 'drive') {
      return this.handleGoogleDriveCallback(code, redirectUri);
    }
    return this.handleGoogleLoginCallback(code, redirectUri);
  }

  getGoogleStatus() {
    return this.getGoogleDriveStatus();
  }

  disconnectGoogle() {
    return this.disconnectGoogleDrive();
  }

  // ─── Meta (Facebook & Instagram Graph API) ───
  getMetaAuthUrl(redirectUri?: string) {
    const query = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '';
    return this.request<{ url: string }>(`/auth/meta/url${query}`);
  }

  handleMetaCallback(code: string, redirectUri?: string) {
    return this.request<{
      success: boolean;
      message: string;
      user_name: string;
      accounts_count: number;
      accounts: any[];
    }>('/auth/meta/callback', {
      method: 'POST',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
  }

  getMetaStatus() {
    return this.request<{
      connected: boolean;
      user_name: string | null;
      accounts_count: number;
      instagram_accounts_count: number;
      accounts: any[];
    }>('/auth/meta/status');
  }

  disconnectMeta() {
    return this.request<{ success: boolean; message: string }>('/auth/meta/disconnect', {
      method: 'POST',
    });
  }

  syncMetaMetrics(clientId: string, data?: { period?: string; instagram_account_id?: string }) {
    return this.request<{
      success: boolean;
      message: string;
      metrics: any;
    }>(`/metrics/sync-meta/${clientId}`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  // ─── Sesión de Usuario ───
  getMe() {
    return this.request<{
      authenticated: boolean;
      user: { email: string; name: string; picture?: string } | null;
    }>('/auth/me');
  }

  // ─── Packages / Planes de Servicio ───
  getPackages() {
    return this.request<{ packages: any[] }>('/packages');
  }

  getPackage(id: string) {
    return this.request<{ package: any }>(`/packages/${id}`);
  }

  createPackage(data: any) {
    return this.request<{ id: string; message: string }>('/packages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updatePackage(id: string, data: any) {
    return this.request<{ message: string }>(`/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  deletePackage(id: string) {
    return this.request<{ message: string }>(`/packages/${id}`, {
      method: 'DELETE',
    });
  }

  assignPackage(id: string, clientIds: string[], updateFee = true) {
    return this.request<{ success: boolean; message: string }>(`/packages/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ client_ids: clientIds, update_fee: updateFee }),
    });
  }

  logout() {
    return this.request<{ success: boolean; message: string }>('/auth/logout', {
      method: 'POST',
    });
  }
}

export const api = new ApiClient(API_BASE);
